'use client';

import { useMemo } from 'react';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { getQuoteLineMtsLocalMap } from '../../../../utils/lead-time';
import { EstimatedDelivery } from '../../../shared/quote-pricing/estimated-delivery';
import { QuoteProductPriceItem } from '../models/quote-product-price-item.model';

interface Props {
   products: QuoteProductPriceItem[];
   selectedProductId: string;
   leadTimeEnabled?: boolean;
   deliveryAddressId?: number;
   onProductChange: (productId: string) => void;
}

export const QuoteProductList = ({ products, selectedProductId, leadTimeEnabled = false, deliveryAddressId, onProductChange }: Props) => {
   const translate = useTranslations();

   const productMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};
      return Object.fromEntries(
         products.map((product) => {
            const lineMtsLocalMap = getQuoteLineMtsLocalMap(product.leadTimeIndicators, product.quotationLines, {
               colorId: product.colorId,
               producerColorCode: product.producerColorCode,
               addressId: deliveryAddressId
            });

            return [product.id, Object.values(lineMtsLocalMap).some(Boolean)];
         })
      );
   }, [products, leadTimeEnabled, deliveryAddressId]);

   return (
      <Paper height="100%">
         <Box direction="column" gap="md">
            <Box justify="between" items="center" gap="sm">
               <Typography variant="body-highlight" color="subtle" id="quoted-products-label">
                  {translate('prices.your-quoted-products')}
               </Typography>
               <Typography variant="small" color="subtle">
                  {translate('prices.products-available', { total: products.length })}
               </Typography>
            </Box>
            <RadioCardGroup ariaLabelledBy="pricing-table-label" value={selectedProductId} onChange={onProductChange}>
               {products.map((product) => {
                  return (
                     <RadioCard key={product.id} value={product.id}>
                        <Box direction="column">
                           <Box gap="sm" items="start">
                              <BoxItem>
                                 <Avatar shape="square" src={product.logoUrl} title={product.brandName} />
                              </BoxItem>
                              <BoxItem grow={1} basis={0}>
                                 <Box direction="column" gap="3xs" items="start">
                                    <Typography variant="body-highlight">{product.description || '-'}</Typography>
                                    <Typography variant="small" color="subtle">
                                       {translate('product.card.product-ref')}:{' '}
                                       {product.customerReferences.length ? product.customerReferences.join(' • ') : '-'}
                                    </Typography>
                                 </Box>
                              </BoxItem>
                           </Box>
                           {productMtsLocalMap[product.id] && <EstimatedDelivery hasMtsLocal justify="start" />}
                        </Box>
                     </RadioCard>
                  );
               })}
            </RadioCardGroup>
         </Box>
      </Paper>
   );
};

